import React, { useEffect, useState, useLayoutEffect, useRef } from "react";
import { DropdownButton } from "react-bootstrap";
import TablePagination from "./Pagination/Pagination";
import DropdownItem from "react-bootstrap/esm/DropdownItem";
import { useAsyncDebounce, useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";
import { AngleDownIcon, SearchIcon } from "./icons";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import useIsMobile from "@hooks/useIsMobile";

function ReactTable({
  title,
  columns,
  data,
  onRowClicked,
  activeId,
  moreFilter,
  buttons,
  pagesize = 20,
  size,
  pagination = true,
  filter = true,
  compact = false,
  manualPagination = false,
  pageCount: controlledPageCount,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  isLoading = false,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  showGlobalFilter = filter,
  showPageSize = filter,
  customContent,
  customContentClassName = "",
}) {
  const isMobile = useIsMobile();
  let pageindex = 0;
  let [pageS, setPageS] = useState(pagesize);

  // Only auto-navigate to selected item's page if no filter is active (for client-side pagination only)
  if (!manualPagination) {
    const index = data.findIndex((entry) => entry.id === +activeId);
    if (index > 0) {
      pageindex = parseInt(index / pageS);
    }
  }

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageSize: pagesize, pageIndex: pageindex },
      manualPagination,
      manualSortBy: manualPagination,
      pageCount: controlledPageCount,
      // For manual/server-side tables, keep pagination and sort state stable across data refreshes
      autoResetPage: !manualPagination,
      autoResetSortBy: !manualPagination,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    setPageSize,
    previousPage,
    state: { pageSize, globalFilter, pageIndex, sortBy },
    setGlobalFilter,
  } = tableInstance;

  // Track previous pageSize to only notify on actual changes
  const prevPageSizeRef = React.useRef(pageSize);
  const prevOrderingRef = React.useRef(null);

  useEffect(() => {
    setPageS(pageSize);

    // Only call onPageSizeChange if pageSize actually changed (not just callback reference)
    if (manualPagination && onPageSizeChange && pageSize !== prevPageSizeRef.current) {
      onPageSizeChange(pageSize);
    }

    prevPageSizeRef.current = pageSize;
  }, [pageSize, manualPagination, onPageSizeChange]);

  useEffect(() => {
    if (!manualPagination) {
      gotoPage(0);
    }
  }, [sortBy, manualPagination]);

  // Notify parent of sort changes for manual pagination
  useEffect(() => {
    if (!manualPagination || !onSortChange) return;

    const nextOrdering = sortBy.length > 0 ? `${sortBy[0].desc ? "-" : ""}${sortBy[0].id}` : "";

    if (prevOrderingRef.current !== nextOrdering) {
      prevOrderingRef.current = nextOrdering;
      onSortChange(nextOrdering);
    }
  }, [sortBy, manualPagination, onSortChange]);

  // Reset to first page when filter changes to avoid being on a page that doesn't exist
  useEffect(() => {
    if (globalFilter !== undefined && !manualPagination) {
      gotoPage(0);
    }
  }, [globalFilter, manualPagination]);

  // For manual pagination: sync ONLY on mount and when parent explicitly changes currentPage
  // Track if this is the first render to set initial page
  const isInitialMount = React.useRef(true);
  const prevCurrentPageRef = React.useRef(currentPage);

  useEffect(() => {
    if (!manualPagination) return;

    const targetIndex = currentPage - 1;

    // Only sync if:
    // 1. Initial mount (to set correct starting page), OR
    // 2. Parent explicitly changed currentPage (and it's different from what we just notified)
    if (isInitialMount.current) {
      if (pageIndex !== targetIndex) {
        gotoPage(targetIndex);
      }
      isInitialMount.current = false;
    } else if (currentPage !== prevCurrentPageRef.current && pageIndex !== targetIndex) {
      // Parent changed currentPage (not from our onPageChange notification)
      gotoPage(targetIndex);
    }

    prevCurrentPageRef.current = currentPage;
  }, [currentPage, manualPagination, pageIndex, gotoPage]);

  // Handle manual pagination page changes (notify parent when user clicks pagination)
  // Use a ref to track the last known external currentPage to detect internal changes
  const lastCurrentPageRef = React.useRef(currentPage);

  useEffect(() => {
    if (manualPagination && onPageChange) {
      const expectedIndex = (lastCurrentPageRef.current || 1) - 1;
      // Only notify if pageIndex changed due to user interaction (not external prop sync)
      if (pageIndex !== expectedIndex && currentPage === lastCurrentPageRef.current) {
        onPageChange(pageIndex + 1);
      }
      lastCurrentPageRef.current = currentPage;
    }
  }, [pageIndex, currentPage, manualPagination, onPageChange]);

  const hasToolbar = Boolean(title) || showGlobalFilter || showPageSize || moreFilter || buttons;
  const wrapperRef = useRef(null);
  const toolbarRef = useRef(null);
  const [toolbarWidth, setToolbarWidth] = useState(0);
  const [availableHeight, setAvailableHeight] = useState(null);

  useLayoutEffect(() => {
    if (!toolbarRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setToolbarWidth(entry.contentRect.width);
    });
    ro.observe(toolbarRef.current);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (compact) return;
    if (!wrapperRef.current) return;

    const recalc = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const next = Math.floor(window.innerHeight - rect.top - 8);
      if (Number.isFinite(next) && next > 220) {
        setAvailableHeight(next);
      }
    };

    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [compact, toolbarWidth, data.length, hasToolbar]);

  const isNarrow = toolbarWidth > 0 && toolbarWidth < 950;

  return (
    <div
      ref={wrapperRef}
      className={`table-wrapper ${compact ? "table-wrapper--compact" : ""}`}
      style={!compact && availableHeight ? { height: `${availableHeight}px` } : undefined}
    >
      {hasToolbar && (
        <div
          ref={toolbarRef}
          className={`table-wrapper__filter d-flex align-items-center gap-2 flex-nowrap ${
            isMobile ? "py-2" : ""
          }`}
        >
          {title && !isMobile && (
            <div className="me-3 flex-shrink-0 d-flex align-items-center" style={{ height: 38 }}>
              {typeof title === "string" ? <h5 className="mb-0">{title}</h5> : title}
            </div>
          )}

          <div
            className="d-flex flex-wrap align-items-center gap-2 flex-grow-1"
            style={{ minWidth: 0 }}
          >
            {showGlobalFilter && (
              <div className="flex-shrink-0" style={{ minWidth: isMobile ? 140 : 220 }}>
                <GlobalFilter
                  globalFilter={globalFilter}
                  setGlobalFilter={setGlobalFilter}
                  externalValue={searchValue}
                  onExternalChange={onSearchChange}
                  searchPlaceholder={searchPlaceholder}
                />
              </div>
            )}

            {showPageSize && (
              <DropdownButton
                title={
                  <div className="d-flex gap-2 align-items-center" title="Page size">
                    <span>{pageSize}</span>
                    {!isMobile && <AngleDownIcon />}
                  </div>
                }
                variant="btn-transparent"
              >
                {size.map((ps, i) => (
                  <DropdownItem key={i} onClick={() => setPageSize(ps)}>
                    {ps}
                  </DropdownItem>
                ))}
              </DropdownButton>
            )}

            {moreFilter}
          </div>

          {buttons && (
            <div
              className={`ms-auto d-flex ${
                isMobile ? "align-items-start" : "align-items-center"
              } gap-2 flex-nowrap flex-shrink-0 align-self-start`}
            >
              {typeof buttons === "function" ? buttons({ isNarrow, toolbarWidth }) : buttons}
            </div>
          )}
        </div>
      )}

      <div className="table-wrapper__container">
        <div className="table-wrapper__container__inner " style={{ width: "100%", minWidth: 0 }}>
          <div
            className={`table-wrapper__table-scroll ${customContent ? customContentClassName : ""}`}
          >
            {customContent ? (
              customContent
            ) : (
              <table {...getTableProps()}>
                <thead>
                  {
                    // Loop over the header rows
                    headerGroups.map((headerGroup, i) => (
                      // Apply the header row props
                      <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                        {
                          // Loop over the headers in each row
                          headerGroup.headers.map((column, i) => (
                            // Apply the header cell props
                            <th
                              key={i}
                              {...column.getHeaderProps([
                                column.getSortByToggleProps(),
                                {
                                  style: column.style,
                                  className: column.className,
                                },
                              ])}
                            >
                              <div>
                                {column.render("Header")}
                                {
                                  <span>
                                    {column.disableSortBy ? (
                                      []
                                    ) : column.isSorted ? (
                                      column.isSortedDesc ? (
                                        <FontAwesomeIcon icon={faSortDown} />
                                      ) : (
                                        <FontAwesomeIcon icon={faSortUp} />
                                      )
                                    ) : (
                                      <FontAwesomeIcon icon={faSort} />
                                    )}
                                  </span>
                                }
                              </div>
                            </th>
                          ))
                        }
                      </tr>
                    ))
                  }
                </thead>
                {/* Apply the table body props */}
                <tbody {...getTableBodyProps()}>
                  {
                    // Loop over the table rows
                    page.map((row, i) => {
                      prepareRow(row);
                      return (
                        // Apply the row props
                        <tr
                          className={+activeId == row.original.id ? "table-wrapper__active" : ""}
                          key={i}
                          {...row.getRowProps({
                            onClick: () => onRowClicked && onRowClicked(row),
                          })}
                        >
                          {
                            // Loop over the rows cells
                            row.cells.map((cell, i) => {
                              // Apply the cell props
                              return (
                                <td key={i} {...cell.getCellProps()}>
                                  {cell.render("Cell")}
                                </td>
                              );
                            })
                          }
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            )}
            {isLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
          </div>
          {pagination && (
            <div className="table-wrapper__pagination">
              <TablePagination
                pageIndex={pageIndex}
                pageOptions={pageOptions}
                previousPage={previousPage}
                canPreviousPage={canPreviousPage}
                nextPage={nextPage}
                canNextPage={canNextPage}
                gotoPage={gotoPage}
                pageSize={pageSize}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReactTable;

function GlobalFilter({
  globalFilter,
  setGlobalFilter,
  externalValue,
  onExternalChange,
  searchPlaceholder,
}) {
  const { t } = useTranslation();
  const isControlled = externalValue !== undefined && onExternalChange;
  const [value, setValue] = React.useState(isControlled ? externalValue : globalFilter);

  const onChange = useAsyncDebounce((value) => {
    if (isControlled) {
      onExternalChange(value || "");
    } else {
      setGlobalFilter(value || undefined);
    }
  }, 500); // Increased to 500ms for server-side search

  // Sync local value with external value (controlled mode) or global filter (uncontrolled mode)
  React.useEffect(() => {
    if (isControlled) {
      setValue(externalValue);
    } else {
      setValue(globalFilter);
    }
  }, [isControlled, externalValue, globalFilter]);

  const isMobile = useIsMobile();

  return (
    <div className="table-wrapper__filter__searchbar">
      <input
        className="form-control"
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={searchPlaceholder || t("search")}
        style={{ maxWidth: isMobile ? "100px" : "200px" }}
      />
      <SearchIcon className="search-icon" />
    </div>
  );
}
